import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useSelector } from 'react-redux'
import useTrans from '@hooks/useTrans'
import Datatable, {
  DatatableRow,
  DatatableCell,
} from '../../atoms/Datatable/Datatable'
import { Alert, Avatar, Filter, HeaderLabel, IncaricatoDropdown, Loading, MyAccountTag, StatoOrdine } from '@components/atoms'
import { FornitoreType, OrdineListType, RootStateType, UtenteType } from '@utils/types'
import styles from './DatatableOrdiniListing.module.sass'
import { cn, dateFormat, getUserType } from '@utils/safeTs'
import _ from 'lodash'
import { Checkmark, Dot } from '@components/icons'
import { ChatDrawer } from '@components/organisms'
import MyAccountDownloadModal from '@components/organisms/MyAccountDownloadModal/MyAccountDownloadModal'
import { StatoOrdineStatus } from '@components/atoms/StatoOrdine/StatoOrdine'
import { ALERT_ORDINI, ALERT_ORDINI_FORNITORE } from '@utils/settings'
import { api } from '@utils/index'
import { ORDINI } from '@utils/endpoint'
import { displayNotification } from '@components/atoms/Notifica/Notifica'

export interface DatatableOrdiniListingProps {
  ordini: OrdineListType[]
  setOrdini?: (v: OrdineListType[]) => void
  fornitori?: FornitoreType[]
  sort?: { key: string, dir: 'asc' | 'desc' }[]
  setSort?: any // (v: { key: string, dir: 'asc' | 'desc' }[]) => void
  actions?: React.ReactNode
}

const DatatableOrdiniListing = ({
  ordini = [],
  setOrdini,
  fornitori = [],
  sort,
  setSort,
  actions,
}: DatatableOrdiniListingProps) => {
  const t = useTrans()
  const { utente } = useSelector((state: RootStateType) => state?.utente)
  const userType = getUserType(utente)
  const alertWhitelist = Object.keys(userType === 'fornitore' ? ALERT_ORDINI_FORNITORE : ALERT_ORDINI)
  const [ordineChat, setOrdineChat] = useState<OrdineListType>(null)
  const [ordineDownload, setOrdineDownload] = useState<OrdineListType>(null)

  const localStorageVisibleColumnsKey = 'ordini.visiblecolumns'
  const [visibleColumns, setVisibleColumns] = useState([]) 
  const [savingIncaricato, setSavingIncaricato] = useState(null)

  const getOrdini = (ordine: OrdineListType) => ordine.isParent ? ordine.ordini || [] : [ordine]
  const getDistinctValues = (ordine: OrdineListType, valueFunc: (ordine: OrdineListType) => React.ReactNode) => (
    _.uniq(getOrdini(ordine).map(o => valueFunc(o)))
  )

  const allCols = {
    documento: {
      label: 'Doc #', 
      sortKey: 'documento',
      mobileCols: 6,
    },
    riferimentoLavorazione: { 
      label: 'Rif. lavorazione',
    },
    ragioneSociale: { 
      label: 'Ragione sociale',
      sortKey: 'ragione_sociale',
    },
    cliente: { 
      label: 'Cliente',
      sortKey: 'cliente',
    },
    tecniche: {
      label: 'Tecniche',
    },
    fornitore: { 
      label: 'Fornitore',
    },
    commerciale: { 
      label: 'Comm.',
      desktopAlign: 'center',
    },
    incaricato: { 
      label: 'Incaricato',
      desktopAlign: 'center',
    },
    statoOrdine: { // Diverso da stato per larghezza e contenuto (non c'è alert dentro e c'è il componente <StatoOrdine.. />)
      label: 'Stato ordine', 
      sortKey: 'stato_ordine_calcolato',
    },
    tracciaturaLogo: { 
      label: 'Tracc. logo',
      desktopAlign: 'center',
    },
    tassativo: { 
      label: 'Tax',
      desktopAlign: 'center',
    },
    // prio: { 
    //   label: 'Prio',
    //   desktopAlign: 'center',
    // },
    bloccato: { 
      label: 'Stand by',
      desktopAlign: 'center',
    },
    created: {
      label: 'Creato il',
      sortKey: 'created',
    },
    finePrevista: { 
      label: 'Fine prevista',
      sortKey: 'data_termine_lavorazione'
    },
    dataEvasione: { 
      label: 'Evaso il',
      sortKey: 'data_evasione'
    },
    alert: { 
      label: 'Alert',
    },
    // chat: { 
    //   label: 'Chat',
    // },
    // download: { 
    //   label: 'Download' 
    // }
  }

  const userTypeOrdiniColsKeys = {
    fornitore:    Object.keys(allCols).filter(k => k !== 'fornitore'),
    commerciale:  Object.keys(allCols).filter(k => k !== 'commerciale'),
    admin:        Object.keys(allCols),
  }

  // non ci devono essere altre casistiche oltre a fornitore, commerciale e admin - cliente non deve accedere a questa pagina
  const userColsKeys = userTypeOrdiniColsKeys[userType] || []
   
  const cols: { [k: string]: any } = userColsKeys.reduce((res, k) => ({
    ...res, 
    [k]: {
      ..._.omit(allCols[k], ['sortKey']), 
      key: k,
      label: <HeaderLabel 
        label={allCols[k].label} 
        wrappable
        sortable={!!allCols[k].sortKey} 
        sortFilter={sort.filter(s => s.key === allCols[k].sortKey)?.[0]?.dir} 
        onClick={() => setSort(prev => [{ 
          key: allCols[k].sortKey,
          dir: (prev.filter(p => p.key === allCols[k].sortKey)?.[0]?.dir || 'asc') === 'desc' ? 'asc' : 'desc' 
        }].concat(prev.filter(p => p.key !== allCols[k].sortKey)).slice(0, 3))} 
      />,
      mobileCols: allCols[k].mobileCols || 6,
    } 
  }), {})
  
  const handleChangeIncaricato = (incaricato: UtenteType, ordine: OrdineListType) => {
    setSavingIncaricato(ordine.id)
    api
      .put(`${ORDINI}${ordine.id}/`, { incaricatoId: incaricato.id })
      .then(() => {
        setOrdini(ordini.map(op => (op.isParent 
          ? {...op, ordini: op.ordini.map(o => o.id === ordine.id 
            ? ({...o, incaricato: incaricato }) 
            : o
            )}
          : op.id === ordine.id ? {...op, incaricato: incaricato } : op
        )))
        displayNotification('changeincaricato_' + ordine.id + "_success", t("Incaricato salvato con successo"), "success")
      })
      .catch((response) => {
        console.log(response)
        displayNotification('changeincaricato_' + ordine.id + "_error", t("Errore durante il salvataggio dell'incaricato"), "error")
      })
      .finally(() => {
        setSavingIncaricato(null)
      })
    
  }

  const ordineHasUnreadChat = (ordine: OrdineListType): boolean => {
    return !!ordine.numMessaggiChatNonVisualizzati
  }

  const handleChatClick = (ordine: OrdineListType) => {
    setOrdineChat(ordine)
  }
  
  const handleDownloadClick = (ordine: OrdineListType) => {
    setOrdineDownload(ordine)
  }

  useEffect(() => {
    const lsVisibleColumns = JSON.parse(localStorage.getItem(localStorageVisibleColumnsKey))
    if (!lsVisibleColumns)
      localStorage.setItem(localStorageVisibleColumnsKey, JSON.stringify(userColsKeys))
    setVisibleColumns(lsVisibleColumns || userColsKeys)
  }, [])

  useEffect(() => {
    if (visibleColumns.length)
      localStorage.setItem(localStorageVisibleColumnsKey, JSON.stringify(visibleColumns))
  }, [visibleColumns])

  return (
    <>
      {userType !== 'cliente' && (
        <div className={styles.actions}>
          {actions}
          <Filter 
            id="filter_dettagli"
            variant='multiple'
            label={t("Dettagli")} 
            keepLabel
            showReset={false}
            value={visibleColumns}
            onChange={(v) => setVisibleColumns(Array.isArray(v) ? v : [])}
            options={userColsKeys.filter(k => k in allCols).map(k => ({ label: t(allCols[k].label), value: k }))} 
            dropdownSide='left'
          />
        </div>
      )}
      <Datatable cols={Object.values(cols).reduce((res, col) => visibleColumns.includes(col.key) ? ({...res, [col.key]: col }) : res, {})} className={cn(styles.root, styles[`root--${userType}`])}>
        {ordini &&
          ordini.map((ordine) => (
            <DatatableRow key={ordine.id} className={cn(styles.row, getDistinctValues(ordine, o => alertWhitelist.includes(o.alertCalcolato) ? o.alertCalcolato : null)?.filter(v => !!v && v !== 'EVASIONE_OLTRE_24H')?.length > 0 ? styles['row--alert'] : '', ordine.bloccato ? styles['row--bloccato'] : '')}>
              {Object.keys(cols).filter(k => visibleColumns.includes(k)).map(k => (
                <DatatableCell col={cols[k]} key={k}>
                  {k === 'documento' && (
                    <Link href={'/myaccount/gestione/ordini/' + (ordine.numeroProtocolloOriginale || ordine.numeroProtocollo)} className={styles.documentoLink}>
                      {getOrdini(ordine).filter(o => o.numeroDocumento)?.[0]?.numeroDocumento?.split('/')?.[2] || 'ND'}
                    </Link>
                  )}
                  {k === 'riferimentoLavorazione' && _.uniq(getOrdini(ordine).map((o) => [o.riferimentoLavorazione, o.numeroDocumento, o.ordineFornitore?.numeroDocumento].join("|"))).map(o => o.split("|")).filter(o => o.filter(Boolean).length > 0).map((o, idx) => (
                    <div className={styles.riferimentoLavorazioneBox} key={idx}>
                      {!!o[0] && <div className={styles.riferimentoLavorazione}>{o[0]}</div>}
                      {!!o[1] && <div className={styles.numeroDocumento}>{o[1]}</div>}
                      {!!o[2] && <div className={styles.numeroDocumento}>{o[2]}</div>}
                    </div>
                  ))}
                  {k === 'statoOrdine' && !!ordine.statoOrdineCalcolato && (
                    <div className={styles.statoOrdineBox}>
                      {getDistinctValues(ordine, o => o.statoOrdineCalcolato)?.length === 1 
                        ? <StatoOrdine stato={getOrdini(ordine)?.[0]?.statoOrdineCalcolato as StatoOrdineStatus} level='ordine' className={cn(styles.statoOrdine, styles[`statoOrdine--${getOrdini(ordine)?.[0]?.statoOrdineCalcolato}`])} />
                        : getOrdini(ordine).map(o => (
                            <StatoOrdine key={o.id} stato={o.statoOrdineCalcolato as StatoOrdineStatus} level='ordine' className={cn(styles.statoOrdine, styles[`statoOrdine--${getOrdini(ordine)?.[0]?.statoOrdineCalcolato}`])} />
                      ))}
                    </div>
                  )}
                  {k === 'alert' && (
                    <div className={styles.alertBox}>
                      {getDistinctValues(ordine, (o) => alertWhitelist.includes(o.alertCalcolato) ? o.alertCalcolato : null)?.length > 1 ? 
                        getOrdini(ordine).map(o => 
                          <Alert 
                            key={o.id} 
                            placeholder={alertWhitelist.includes(o.alertCalcolato) ? ALERT_ORDINI[o.alertCalcolato] : t("Regolare")} 
                            styleVariant='basic' 
                            icon={alertWhitelist.includes(o.alertCalcolato) ? undefined : <Dot />}
                            size="sm" 
                            variant={alertWhitelist.includes(o.alertCalcolato) ? o.alertCalcolato === 'EVASIONE_OLTRE_24H' ? 'warning' : 'danger' : 'success'} 
                          />
                      ) 
                      : getDistinctValues(ordine, (o) => alertWhitelist.includes(o.alertCalcolato) ? o.alertCalcolato : null).map((v: string) => 
                        <Alert 
                          key={v} 
                          placeholder={v ? ALERT_ORDINI[v] : t("Regolare")} 
                          styleVariant='basic' 
                          icon={v ? undefined : <Dot />}
                          size="sm" 
                          variant={v ? v === 'EVASIONE_OLTRE_24H' ? 'warning' : 'danger' : 'success'} 
                        />
                      )}
                    </div>
                  )}
                  {k === 'ragioneSociale' && getDistinctValues(ordine, (o) => o.anagraficaAzienda?.ragioneSociale)}
                  {k === 'cliente' && _.uniq(getOrdini(ordine).map((o) => (
                    [[o.utente?.cognome, o.utente?.nome].filter(Boolean).join(" "), o.riferimentoCliente].join("|")
                  ))).map(o => o.split("|")).map((o, idx) => (
                    <React.Fragment key={idx}>
                      {!!o[0] && <div className={styles.cliente}>{o[0]}</div>}
                      {!!o[1] && (
                        <div className={styles.riferimentoCliente}>{o[1]}</div>
                      )}
                    </React.Fragment>
                  ))}
                  {k === 'tecniche' && (
                    <div className={styles.tecniche}>
                      {getOrdini(ordine).map(o => (
                        <div key={o.id} className={styles.tecnicheRow}>
                          {_.uniq(o.tecniche?.map(t => t.codiceWebParent))
                            .sort((a, b) => a < b ? -1 : 1).map(c => (
                              <MyAccountTag label={c} variant="bg" color='primary' key={c}/>
                            ))}
                        </div>
                      ))}
                    </div>
                  )}
                  {k === 'incaricato' && (
                    <div className={styles.incaricatoBox}>
                      {userType === 'fornitore' ? (
                        getOrdini(ordine).map(o => (
                          <React.Fragment key={o.id}>
                            {savingIncaricato === o.id ? (
                              <Loading width="20" height="20" />
                            ) : (              
                              <IncaricatoDropdown  
                                key={o.id}
                                size="sm"
                                value={o.incaricato?.id}
                                onChange={(incaricato) => handleChangeIncaricato(incaricato, o)}
                                options={fornitori.filter(f => f.id === o.fornitore?.id)?.[0]?.utenti} 
                                disabled={!!savingIncaricato}
                              />
                            )}
                          </React.Fragment>
                        ))
                      ) : (
                        getOrdini(ordine).map(o => (
                          <div key={o.id}><Avatar size="sm" id={`incaricato_${o.id}`} key={o.id} variant={'incaricato'} utente={o.incaricato} /></div>
                        )
                      ))}
                    </div>
                  )}
                  {k === 'tracciaturaLogo' && (
                    getOrdini(ordine).filter(o => !!o.tracciaturaLogo)?.length ? <span className={styles.check}><Checkmark /></span> : <></>
                  )}
                  {k === 'tassativo' && (
                    getOrdini(ordine).filter(o => !!o.tassativo)?.length ? <MyAccountTag label={t("Tax")} variant='border' color="danger" className={styles.tax} /> : <></>
                  )}
                  {/* {k === 'prio' && (
                    getOrdini(ordine).filter(o => !!o.prio)?.length ? <span className={styles.check}><Checkmark /></span> : <></>
                  )} */}
                  {k === 'bloccato' && (
                    getOrdini(ordine).filter(o => !!o.bloccato)?.length ? <span className={styles.check}><MyAccountTag label={t("B")} variant='border' color='dark-grey' /></span> : <></>
                  )}
                  {k === 'created' && dateFormat(ordine.created)}
                  {k === 'finePrevista' && (
                    ordine.dataTermineLavorazione ? (
                      getOrdini(ordine).filter(o => o.dataTermineLavorazione)?.length 
                        ? getDistinctValues(ordine, (o) => o.dataTermineLavorazione)?.length === 1 
                          ? getDistinctValues(ordine, (o) => o.dataTermineLavorazioneForzata 
                            ? <MyAccountTag label={dateFormat(o.dataTermineLavorazione)} variant="border" color='warning' /> 
                            : dateFormat(o.dataTermineLavorazione))?.[0] || '-'
                          : getOrdini(ordine).map(o => o.dataTermineLavorazione 
                            ? o.dataTermineLavorazioneForzata 
                              ? <MyAccountTag label={dateFormat(o.dataTermineLavorazione)} variant="border" color='warning' /> 
                              : <div>{dateFormat(o.dataTermineLavorazione)}</div> : <div>-</div>
                            )
                        : <></>
                    ) : <></>
                  )}
                  {k === 'dataEvasione' && 
                    getOrdini(ordine).filter(o => o.dataEvasione)?.length 
                      ? getDistinctValues(ordine, (o) => o.dataEvasione)?.length === 1 
                        ? getDistinctValues(ordine, (o) => dateFormat(o.dataEvasione))?.[0] || '-'
                        : getOrdini(ordine).map(o => (<div>{o.dataEvasione ? dateFormat(o.dataEvasione) : '-'}</div>)) 
                      : <></>
                  }
                  {k === 'commerciale' && ordine.commerciale ? <div><Avatar size="sm" utente={ordine.commerciale} variant="chat" className={styles.commercialeAvatar} /></div> : <></>} 
                  {k === 'fornitore' && (
                    getDistinctValues(ordine, o => o.fornitore?.sigla || o.fornitore?.ragioneSociale)?.length === 1 
                    ? <div className={styles.fornitori}>
                      {getOrdini(ordine)?.[0]?.fornitore ? <span>{getOrdini(ordine)?.[0]?.fornitore.sigla || getOrdini(ordine)?.[0]?.fornitore.ragioneSociale}</span> : <></>}
                    </div>
                    : <div className={styles.fornitori}>
                      {getOrdini(ordine).map(o => <span key={o.id}>{o.fornitore ? o.fornitore.sigla || o.fornitore.ragioneSociale : '-'}</span>)}
                    </div> 
                  )}
                  {/* {k === 'chat' && <div>
                    <ButtonRounded 
                      label={t("Chat")} 
                      badge={ordineHasUnreadChat(ordine) ? { type: 'dot' } : undefined} 
                      size='sm' 
                      onClick={() => handleChatClick(ordine)}
                    />
                  </div>}
                  {k === 'download' && <div>
                    <ButtonRounded label={t("Download")} size='sm' onClick={() => handleDownloadClick(ordine)} />
                  </div>} */}
                </DatatableCell>
              ))}
            </DatatableRow>
          ))}
      </Datatable>
      <ChatDrawer 
        open={!!ordineChat}
        ordine={ordineChat}
        onMessageView={(n) => setOrdini(ordini.map(o => o.id === ordineChat?.id ? {...o, numMessaggiChatNonVisualizzati: n} : o))}
        onClose={() => setOrdineChat(null)}
      />
      <MyAccountDownloadModal 
        ordine={ordineDownload}
        open={!!ordineDownload}
        title={t("Download")}
        description={t("Seleziona i file che desideri scaricare")}
        onClose={() => setOrdineDownload(null)}
      />
    </>
  )
}

export default DatatableOrdiniListing